export type DateFormatToken =
  | {type: 'digits'; length: number}
  | {type: 'separator'; char: string};

export function parseDateInputFormat(format: string): DateFormatToken[] {
  const tokens: DateFormatToken[] = [];
  let i = 0;
  while (i < format.length) {
    const ch = format[i];
    if (/[a-zA-Z]/.test(ch)) {
      let len = 1;
      while (i + len < format.length && format[i + len] === ch) len++;
      tokens.push({type: 'digits', length: len});
      i += len;
    } else {
      tokens.push({type: 'separator', char: ch});
      i += 1;
    }
  }
  return tokens;
}

export function getMaxDigits(tokens: DateFormatToken[]): number {
  return tokens.reduce(
    (sum, token) => (token.type === 'digits' ? sum + token.length : sum),
    0,
  );
}

export function autoFormatDateInput(raw: string, tokens: DateFormatToken[]): string {
  const digitTokens = tokens.filter(
    (token): token is {type: 'digits'; length: number} => token.type === 'digits',
  );
  const sepToken = tokens.find(
    (token): token is {type: 'separator'; char: string} => token.type === 'separator',
  );
  const sepChar = sepToken?.char ?? '-';

  const segments: string[] = [];
  let segIdx = 0;
  let current = '';
  let endedWithSeparator = false;

  for (let i = 0; i < raw.length; i++) {
    if (segIdx >= digitTokens.length) break;
    const ch = raw[i];
    const segLen = digitTokens[segIdx].length;

    if (/\d/.test(ch)) {
      if (current.length >= segLen) {
        segments.push(current);
        segIdx++;
        if (segIdx >= digitTokens.length) {
          current = '';
          endedWithSeparator = false;
          break;
        }
        current = ch;
      } else {
        current += ch;
      }
      endedWithSeparator = false;
    } else if (current.length > 0) {
      // Non-digit acts as a "move on" marker. Pad short day/month segments
      // with a leading zero (e.g. `1-` → `01-`); skip the marker for
      // multi-digit segments like the year to avoid weird padding.
      if (current.length < segLen) {
        if (segLen > 2) continue;
        current = current.padStart(segLen, '0');
      }
      segments.push(current);
      current = '';
      segIdx++;
      endedWithSeparator = true;
    }
  }

  if (current.length > 0) {
    segments.push(current);
    endedWithSeparator = false;
  }

  let result = segments.join(sepChar);
  if (endedWithSeparator && segments.length < digitTokens.length) {
    result += sepChar;
  }
  return result;
}
